import * as React from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import moment from 'moment-timezone';

import config from 'config';
import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { ReduxState } from 'ducks';
import { useBounds } from 'hooks/useBounds';
import { selectInitialDateFormValues } from 'components/DateFormPanel/DateFormValues';
import { getConfigQueryParams } from 'lib/util/getConfigQueryString';
import { DateForm } from 'components/DateFormPanel/DateForm';
import { DateFormFreeStartDateTime } from 'components/DateFormPanel/DateFormFreeStartDateTime';
import { DateFormValues } from 'components/DateFormPanel/DateFormValues';
import { getThemeCssOverride } from 'components/Themes';
import { useLastProductInstanceId } from 'hooks/useLastProductInstanceId';
import { selectInitialGuestCount } from 'lib/util/guestSelectUtils';
import { getGuestTypesUsedInProduct } from 'lib/util/getGuestTypesUsedInProduct';
import { useCustomerLedgerEnabled } from 'hooks/useCustomerLedgerEnabled';

const postHeightChangedMessage = (id: string, height: number) => {
  window.parent.postMessage(
    {
      type: 'HEIGHT_CHANGED',
      id,
      height,
    },
    '*'
  );
};

const postBeginCheckoutMessage = (url: string) => {
  window.parent.postMessage(
    {
      type: 'BEGIN_CHECKOUT',
      url,
    },
    '*'
  );
};

const postCloseModalMessage = () => {
  window.parent.postMessage(
    {
      type: 'CLOSE_MODAL',
    },
    '*'
  );
};

interface OwnProps {
  isEmbedded: boolean;
  showPrices: boolean;
  onNextClick?: (productInstanceId: string, props: { [key: string]: string }) => void;
  ignoreExistingReservationStartDateTimeAndGuestCount?: boolean;
  hideCalendar?: boolean;
  buttonLabel?: string;
}

type Props = OwnProps;

export const DateFormPanel = ({
  isEmbedded,
  showPrices,
  onNextClick,
  ignoreExistingReservationStartDateTimeAndGuestCount,
  hideCalendar,
  buttonLabel,
}: Props) => {
  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);
  const router = useRouter();
  const { i18n, t } = useTranslation();
  const { apiKey, useApiKeyInPaths } = React.useContext(ApiKeyContext);
  const product = useSelector((state: ReduxState) => state.server.product.product);
  const initialValuesByExistingReservation = useSelector((state: ReduxState) =>
    selectInitialDateFormValues(state, { t })
  );
  const initialGuestCount = useSelector((state: ReduxState) =>
    selectInitialGuestCount(state, { t })
  );
  const productGuestTypes = product ? getGuestTypesUsedInProduct(product, t) : [];

  const [dateFormRef, dateFormBounds] = useBounds<HTMLDivElement>();

  const lastProductInstanceId = useLastProductInstanceId();

  const customerLedgerEnabled = useCustomerLedgerEnabled();

  const existingReservationId = router.query.r as string;

  const isDisplayingInIframe =
    typeof window !== 'undefined' && window.location !== window.parent.location;

  React.useEffect(() => {
    if (isEmbedded && window.parent && product) {
      // When running embedded, the embed script will receive the HEIGHT_CHANGED message and
      // resize the enclosing iframe.
      postHeightChangedMessage(product.id, dateFormBounds.height);
    }
  }, [dateFormBounds.height, isEmbedded, product]);

  const initialValues = ignoreExistingReservationStartDateTimeAndGuestCount
    ? {
        guestCount: initialGuestCount,
        date: moment().format('YYYY-MM-DD'),
      }
    : initialValuesByExistingReservation;

  // If query parameters with initial values are provided, override the initial values.
  const initialDateFromQuery = (router.query.dt ?? '') as string;
  if (initialDateFromQuery) {
    initialValues.date = initialDateFromQuery;
  }
  const initialGuestCountFromQuery = (router.query.gc ?? '') as string;
  if (initialGuestCountFromQuery) {
    const guestCount = JSON.parse(initialGuestCountFromQuery);
    [
      { key: 'Adult', translatedKeys: ['Adult', '大人'] },
      { key: 'Child', translatedKeys: ['Child', '子供'] },
      { key: 'Infant', translatedKeys: ['Infant', '幼児'] },
    ].forEach((guestType) => {
      if (guestCount[guestType.key]) {
        const productGuestType = productGuestTypes.find((g) =>
          guestType.translatedKeys.includes(g.key)
        );
        if (productGuestType) {
          const maxPaxRule = product?.booking_widget_settings?.maximum_participant_rules.find(
            (rule) => rule.unit === productGuestType.key
          );
          const existingGuestCount = initialValues.guestCount[productGuestType.key] ?? 0;
          initialValues.guestCount[productGuestType.key] = Math.min(
            Math.max(existingGuestCount, guestCount[guestType.key]),
            maxPaxRule?.maximum_participants ?? Number.MAX_SAFE_INTEGER
          );
        }
      }
    });
  }

  if (!product) {
    return null;
  }

  const onClose = () => {
    if (isDisplayingInIframe) {
      postCloseModalMessage();
    } else {
      router.back();
    }
  };

  const isUpgrade = Boolean(router.query.u);

  const isModal = Boolean(router.query.isModal);

  const openNewTab = (): boolean => {
    return config.enableCustomerLedger && customerLedgerEnabled;
  };

  return (
    <Form<DateFormValues>
      keepDirtyOnReinitialize={true}
      initialValues={initialValues}
      onSubmit={async (values: DateFormValues) => {
        const { productInstanceId: selectedProductInstanceId, guestCount } = values;

        const productInstanceId = product?.booking_widget_settings?.is_free_start_date_time
          ? lastProductInstanceId
          : selectedProductInstanceId;

        let checkoutPathname = '';
        let query: { [key: string]: string } = {};
        if (productInstanceId && product) {
          query = {
            productInstanceId,
            guestCount: JSON.stringify(guestCount),
            ...getConfigQueryParams(router.query, i18n.language),
          };

          if (existingReservationId) {
            query['r'] = existingReservationId;
          }
          if (router.query.u) {
            query['u'] = '1';
          }
          if (router.query.a) {
            query['a'] = '1';
          }

          checkoutPathname = `/products/${product.id}/${router.query.a ? 'add' : 'checkout'}`;
          if (useApiKeyInPaths) {
            checkoutPathname = `/${apiKey}${checkoutPathname}`;
          } else if (router.asPath.includes('/top')) {
            checkoutPathname = `/top${checkoutPathname}`;
          }
        }

        if (onNextClick) {
          onNextClick(productInstanceId ?? '', query);
        } else if (isEmbedded && isDisplayingInIframe) {
          const searchParams = new URLSearchParams();
          Object.keys(query).forEach((key) => {
            const value = query[key] as string;
            searchParams.append(key, value);
          });
          if (openNewTab()) {
            const path = `${window.location.origin}${checkoutPathname}?${searchParams.toString()}`;
            window.open(path, '_blank');
          } else {
            // When embedded, we post a BEGIN_CHECKOUT message with the checkout URL and the embed script
            // will open the checkout page in a new window.
            postBeginCheckoutMessage(
              `${window.location.origin}/embed${checkoutPathname}?${searchParams.toString()}`
            );
          }
        } else if (isModal) {
          return router.push({
            pathname: `/embed${checkoutPathname}`,
            query,
          });
        } else {
          return router.push({
            pathname: checkoutPathname,
            query,
          });
        }
      }}
    >
      {(props) => (
        <div
          ref={dateFormRef}
          className={clsx(
            (!isEmbedded && getThemeCssOverride(bookingWidgetTheme, 'widget-calendar')) ||
              'widget-calendar'
          )}
        >
          <p className="widget-calendar__headline">
            {isUpgrade ? t('Upgrade booking') : t('Make a booking')}
            {isModal && (
              <p className="widget-calendar__header__close" onClick={onClose}>
                <span />
              </p>
            )}
          </p>
          {config.enableETicket ? (
            <>
              {product?.booking_widget_settings?.is_free_start_date_time ? (
                <DateFormFreeStartDateTime
                  showPrices={showPrices}
                  {...props}
                  loading={false}
                  lastProductInstanceId={lastProductInstanceId}
                  hideDateRange={hideCalendar}
                  buttonLabel={buttonLabel}
                />
              ) : (
                <DateForm
                  showPrices={showPrices}
                  {...props}
                  loading={false}
                  hideCalendar={hideCalendar}
                  buttonLabel={buttonLabel}
                />
              )}
            </>
          ) : (
            <DateForm
              showPrices={showPrices}
              {...props}
              loading={false}
              hideCalendar={hideCalendar}
              buttonLabel={buttonLabel}
            />
          )}
        </div>
      )}
    </Form>
  );
};
